import React, { FunctionComponent, memo } from 'react'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Grid from '@sport1/react-elements/Grid'
import Gradient from '@sport1/react-elements/Gradient'
import withTheme from '@sport1/react-elements/withTheme'
import Touchable from '@sport1/react-elements/Touchable'
import Indicator, { IndicatorProps } from '../../BaseComponents/ProgressBar/ProgressIndicator'
import Image from '../../BaseComponents/Image'
import ThemeInterface from '../../themes/ThemeInterface'
import lightTheme from '../../themes/LightTheme'
import { AudioPlayerProps } from '../AudioPlayerProps'
import BodyFont from '../../Theme/Fonts/Body'
import SmallBodyFont from '../../Theme/Fonts/SmallBody'

export type MiniAudioPlayerProps = AudioPlayerProps &
    IndicatorProps & {
        /** the callback when the user presses on the player
         *
         * @TJS-type function
         */
        onPress?: () => void
        /**
         * the theme of the styleguide which is used for the gradient colors because the gradient cannot use theme strings
         *
         * @defaultValue lightTheme
         */
        theme?: ThemeInterface
    }

const MiniAudioPlayer: FunctionComponent<MiniAudioPlayerProps> = ({
    /* AudioPlayerProps */
    paused = true,
    title,
    episode,
    imageUrl,
    alt,
    onPlayerStateChange,
    onClose,
    onBackward,
    /* IndicatorProps */
    indicatorPercent,
    /* MiniAudioPlayerProps */
    onPress,
    theme = lightTheme,
    /* TestID Props */
    testID,
}) => {
    return (
        <Touchable onPress={onPress} testID={testID} activeOpacity={1}>
            <Grid
                noExpand
                centerVertical
                backgroundColor="BLACK"
                borderTopLeftRadius={8}
                borderTopRightRadius={8}
                position="relative"
                paddingTop={3}
            >
                <Grid
                    position="absolute"
                    width={`${indicatorPercent}%`}
                    height={62}
                    opacity={0.4}
                    zIndex={1}
                    marginBottom={3}
                >
                    <Gradient expand gradient={[theme.colors.ORANGE, 'transparent']} />
                </Grid>
                <Grid noExpand horizontal centerVertical backgroundColor="transparent" zIndex={2}>
                    <Grid noExpand marginLeft={5} marginRight={5} marginBottom={3}>
                        <Image
                            size="S"
                            imageUrl={imageUrl}
                            alt={alt}
                            width={44}
                            radiusType="rounded"
                            aspectRatio="1:1"
                        />
                    </Grid>
                    <Grid minWidth={0}>
                        <BodyFont color="WHITE" numberOfLines={1} bold>
                            {title}
                        </BodyFont>
                        <SmallBodyFont color="keshi-3" numberOfLines={1}>
                            {episode}
                        </SmallBodyFont>
                    </Grid>
                    <Grid noExpand horizontal height="100%">
                        <Touchable onPress={onBackward}>
                            <Grid center paddingLeft={5} paddingRight={4}>
                                <FontelloIcon name="replay-15" color="WHITE" fontSize={18} />
                            </Grid>
                        </Touchable>
                        <Touchable onPress={onPlayerStateChange}>
                            <Grid center paddingX={4}>
                                <FontelloIcon
                                    name={paused ? 'play-active' : 'pause-active'}
                                    color="WHITE"
                                    fontSize={18}
                                />
                            </Grid>
                        </Touchable>
                        <Touchable onPress={onClose}>
                            <Grid center paddingLeft={4} paddingRight={5}>
                                <FontelloIcon name="close-x" color="WHITE" fontSize={18} />
                            </Grid>
                        </Touchable>
                    </Grid>
                </Grid>
                <Indicator indicatorPercent={indicatorPercent} />
            </Grid>
        </Touchable>
    )
}

export default memo(withTheme(MiniAudioPlayer)) as FunctionComponent<MiniAudioPlayerProps>
